import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/axios';
import '../styles/AuditLog.css';
import { Tabs,message } from 'antd';
import { Table, Modal } from 'antd';

const AuditLog = () => {
    const [activeTab, setActiveTab] = useState('db');  // ✅ Tab state
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filters, setFilters] = useState({
        username: '', action: '', entity: '', entity_id: '', sql_query: '', params: '',
        timestamp_from: '', timestamp_to: ''
    });

    const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });

    const debounceRef = useRef(null);

    //modal to display full SQL Query
    const [sqlModal, setSqlModal] = useState({ visible: false, content: '' });


    // prevent rca ...reset to pag1 on click of next
    useEffect(() => {
        // IMMEDIATE fetch for: page, tab, sort changes (filters unchanged)
        const hasFilters = Object.values(filters).some(v => v !== '');
        if (!hasFilters) {
            fetchLogs(page, filters);
            return;
        }

        // DEBOUNCED fetch ONLY when filters ACTUALLY change
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            const newPage = hasFilters ? 1 : page;  // Reset only if filtered
            setPage(newPage);
            fetchLogs(newPage, filters);
        }, 400);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [page, activeTab, sortConfig, filters]);

    // ✅ Modified fetchLogs - tab-aware endpoint
    const fetchLogs = useCallback(async (currentPage = page, currentFilters = filters) => {

        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: currentPage,
                pageSize: 20,
                username: currentFilters.username || '',
                action: currentFilters.action || '',
                entity: currentFilters.entity || '',
                entity_id: currentFilters.entity_id || '',
                sql_query: currentFilters.sql_query || '',
                params: currentFilters.params || '',
                timestamp_from: currentFilters.timestamp_from || '',
                timestamp_to: currentFilters.timestamp_to || '',
                sort: sortConfig.key,
                order: sortConfig.direction
            });

            // ✅ TAB ENDPOINT SWITCH
            const endpoint = activeTab === 'file' ? '/auditlog/file-logs' : '/auditlog/logs';
            //console.log('🔍 Fetching:', endpoint, params.toString());

            //console.log('🐛 activeTab:', activeTab, 'endpoint:', endpoint);
            if (activeTab === 'file' && endpoint !== '/auditlog/file-logs') {
                console.error('🚨 TAB BUG: file tab calling wrong endpoint!');
            }

            const response = await api.get(`${endpoint}?${params}`);
            setLogs(response.data.data || []);
            setTotalPages(response.data.pagination?.totalPages || 1);

            // ✅ Tab info
            if (activeTab === 'file' && response.data.source === 'file') {
                message.info(`${response.data.total || 0} live records from ${response.data.fileDate}`);
            }
        } catch (error) {
            console.error('Audit logs error:', error.response?.data || error);
        } finally {
            setLoading(false);
        }
    }, [activeTab, page, filters, sortConfig]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
        setPage(1);
    };

    const handleClearFilters = () => {
        const emptyFilters = {
            username: '', action: '', entity: '', entity_id: '', params: '', sql_query: '',
            timestamp_from: '', timestamp_to: ''  // ✅ Dates cleared
        };
        setFilters(emptyFilters);
        setPage(1);  // ✅ Reset page
        setSortConfig({ key: 'id', direction: 'desc' });  // ✅ Reset sort
    };

    const formatTimestamp = (timestamp) => new Date(timestamp).toLocaleString();


    return (
        <div className="audit-log-page">
            <div className="page-title-section">
                <h2>Audit Log</h2>
                <button className="btn-secondary" onClick={handleClearFilters}>
                    Clear Filters
                </button>
            </div>

            {/* ✅ TABS WRAP - MINIMAL CHANGE */}
            <Tabs
                activeKey={activeTab}
                onChange={(key) => {
                    //console.log('🔄 Tab changed to:', key);  // ✅ Debug
                    setActiveTab(key);
                    setPage(1);  // ✅ Reset page on tab switch
                }}
                className="audit-tabs"
            >
                <Tabs.TabPane tab="Database (Historical)" key="db" />
                <Tabs.TabPane tab="Today File (Live)" key="file" />
            </Tabs>


            <div className="filters-row">
                <input name="username" placeholder="Username" value={filters.username}
                       onChange={handleFilterChange} className="form-input" />
                <select name="action" value={filters.action} onChange={handleFilterChange} className="form-select">
                    <option value="">Action</option>
                    <option value="INSERT">INSERT</option>
                    <option value="UPDATE">UPDATE</option>
                    <option value="DELETE">DELETE</option>
                </select>
                <input name="entity" placeholder="Entity" value={filters.entity}
                       onChange={handleFilterChange} className="form-input" />
                <input name="entity_id" placeholder="Entity ID" value={filters.entity_id}
                       onChange={handleFilterChange} className="form-input" />
                <div className="date-filter-group">
                    <label>From Date</label>
                    <input
                        type="date"
                        name="timestamp_from"
                        value={filters.timestamp_from}
                        onChange={handleFilterChange}
                        className="form-input date-input"
                    />
                </div>
                <div className="date-filter-group">
                    <label>To Date</label>
                    <input
                        type="date"
                        name="timestamp_to"
                        value={filters.timestamp_to}
                        onChange={handleFilterChange}
                        className="form-input date-input"
                    />
                </div>

                <input name="params" placeholder="Params" value={filters.params}
                       onChange={handleFilterChange} className="form-input" />
                <input name="sql_query" placeholder="SQL Query" value={filters.sql_query}
                       onChange={handleFilterChange} className="form-input" />
                <button className="btn-secondary" onClick={handleClearFilters}>
                    Clear Filters
                </button>
            </div>

            <div className="table-card">
                {loading ? (
                    <div className="table-placeholder">Loading...</div>
                ) : logs.length === 0 ? (
                    <div className="table-placeholder">No records found</div>
                ) : (
                    <table className="data-table">
                        <thead>
                        <tr>
                            <th className={sortConfig.key === 'id' ? 'sorted' : ''} onClick={() => handleSort('id')}>
                                ID{sortConfig.key === 'id' && (sortConfig.direction === 'asc' ? ' ↑' : ' ↓')}
                            </th>
                            <th className={sortConfig.key === 'username' ? 'sorted' : ''} onClick={() => handleSort('username')}>
                                Username{sortConfig.key === 'username' && (sortConfig.direction === 'asc' ? ' ↑' : ' ↓')}
                            </th>
                            <th className={sortConfig.key === 'action' ? 'sorted' : ''} onClick={() => handleSort('action')}>
                                Action{sortConfig.key === 'action' && (sortConfig.direction === 'asc' ? ' ↑' : ' ↓')}
                            </th>
                            <th className={sortConfig.key === 'entity' ? 'sorted' : ''} onClick={() => handleSort('entity')}>
                                Entity{sortConfig.key === 'entity' && (sortConfig.direction === 'asc' ? ' ↑' : ' ↓')}
                            </th>
                            <th className={sortConfig.key === 'entity_id' ? 'sorted' : ''} onClick={() => handleSort('entity_id')}>
                                Entity ID{sortConfig.key === 'entity_id' && (sortConfig.direction === 'asc' ? ' ↑' : ' ↓')}
                            </th>
                            <th className={sortConfig.key === 'timestamp' ? 'sorted' : ''} onClick={() => handleSort('timestamp')}>
                                Timestamp{sortConfig.key === 'timestamp' && (sortConfig.direction === 'asc' ? ' ↑' : ' ↓')}
                            </th>
                            <th>Params</th>
                            <th>SQL Query</th>
                        </tr>
                        </thead>
                        <tbody>
                        {logs.map(log => (
                            <tr key={log.id}>
                                <td>{log.id}</td>
                                <td>{log.username}</td>
                                <td className={`action-${log.action?.toLowerCase()}`}>{log.action}</td>
                                <td>{log.entity}</td>
                                <td>{log.entity_id || '-'}</td>
                                <td>{formatTimestamp(log.timestamp)}</td>
                                <td className="params-cell">
                                    <span
                                        className="params-preview truncate"
                                        title={JSON.stringify(log.params || [])}
                                        onClick={() => setSqlModal({
                                            visible: true,
                                            content: `SQL:\n${log.sql_query}\n\nParams:\n${JSON.stringify(log.params || [], null, 2)}`
                                        })}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {log.params ? `[${log.params.length}]` : '-'}
                                    </span>
                                </td>
                                <td className="sql-cell">
                                    <span
                                        className="sql-preview truncate"
                                        onClick={() => setSqlModal({
                                            visible: true,
                                            content: `SQL:\n${log.sql_query}\n\nParams:\n${JSON.stringify(log.params || [], null, 2)}`
                                        })}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {log.sql_query?.substring(0, 50) || '-'}...
                                    </span>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                )}
            </div>

            {totalPages > 1 && (
                <div className="pagination-row">
                    <button
                        className={`btn-pag ${page === 1 ? 'disabled' : ''}`}
                        disabled={page === 1}
                        onClick={() => setPage(p => p - 1)}
                    >
                        Previous
                    </button>
                    <span className="page-info">Page {page} of {totalPages}</span>
                    <button
                        className={`btn-pag ${page === totalPages ? 'disabled' : ''}`}
                        disabled={page === totalPages}
                        onClick={() => setPage(p => p + 1)}
                    >
                        Next
                    </button>
                </div>
            )}
            <Modal
                title={`Full SQL Query - ${activeTab === 'file' ? 'Live File' : 'Database'}`}
                open={sqlModal.visible}
                onCancel={() => setSqlModal({ visible: false, content: '' })}
                width={900}
                footer={null}
                closable={true}
                maskClosable={true}
            >
                <pre className="full-sql-modal">{sqlModal.content}</pre>
            </Modal>
        </div>


    );
};

export default AuditLog;
